import { useState } from "react";
import { trackEvent } from "../lib/api";
import { submitImplementationRequest } from "../lib/api/index";
import { logger } from "../lib/logger";

const TIERS = [
  { id: "starter", label: "Starter — $750–$1,500" },
  { id: "core", label: "Core — $2,500–$5,000" },
  { id: "advanced", label: "Advanced — $5,000–$10,000+" },
  { id: "enterprise", label: "Enterprise — Custom" },
];

export default function ImplementationModal({ open, onClose, sessionId, defaultEmail, defaultBusiness }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState(defaultEmail || "");
  const [business, setBusiness] = useState(defaultBusiness || "");
  const [tier, setTier] = useState("core");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!name.trim() || !email.trim() || !business.trim()) {
      setError("Please fill out name, email, and business.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await submitImplementationRequest({
        session_id: sessionId,
        name: name.trim(),
        email: email.trim(),
        business: business.trim(),
        preferred_tier: tier,
        notes: notes.trim(),
      });
      // The server fires implementation_request_submit; no need to duplicate
      if (data?.ok) setSubmitted(true);
    } catch (err) {
      logger.error("implementation_request_failed", { sessionId, err: err?.response?.data || err?.message });
      if (err?.response?.status === 429) {
        setError("Too many requests. Please try again in a few minutes.");
      } else if (err?.response?.status === 402) {
        setError("This audit hasn't been unlocked. Please complete checkout first.");
      } else {
        setError("Could not submit your request. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="impl-modal"
      style={{
        position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.78)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="sf-card" style={{ width: "100%", maxWidth: 560, maxHeight: "92vh", overflowY: "auto" }}>
        {submitted ? (
          <div data-testid="impl-modal-thanks">
            <div className="sf-eyebrow" style={{ marginBottom: 10 }}>Request submitted</div>
            <h2 className="sf-display" style={{ fontSize: 24, marginBottom: 10, lineHeight: 1.2 }}>
              Thanks — a SystemForge consultant will reach out within 24 hours.
            </h2>
            <p className="sf-muted-text" style={{ marginBottom: 22 }}>
              We'll review your audit and respond with a tailored implementation proposal.
            </p>
            <button type="button" className="sf-btn" onClick={onClose} data-testid="impl-modal-close-btn">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="impl-modal-form">
            <div className="sf-eyebrow" style={{ marginBottom: 10 }}>Request implementation</div>
            <h2 className="sf-display" style={{ fontSize: 24, marginBottom: 10, lineHeight: 1.2 }}>
              Request implementation support.
            </h2>
            <p className="sf-muted-text" style={{ marginBottom: 14 }}>
              Share your audit with SystemForge so we can review your findings, understand the gaps, and determine whether deeper implementation support is the right next step.
            </p>
            <p className="sf-muted-text" style={{ marginBottom: 24, fontSize: 13, lineHeight: 1.55 }}>
              This is not an automatic contract. The next step is a conversation — we review every request before any work starts or any cost is committed. If we move forward, deeper support may include systems, automation, workflows, and operations support.
            </p>

            <label className="sf-mono" style={{ fontSize: 12, color: "var(--sf-muted)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="sf-input" style={{ marginTop: 6, marginBottom: 16 }} required data-testid="impl-name-input" />

            <label className="sf-mono" style={{ fontSize: 12, color: "var(--sf-muted)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="sf-input" style={{ marginTop: 6, marginBottom: 16 }} required data-testid="impl-email-input" />

            <label className="sf-mono" style={{ fontSize: 12, color: "var(--sf-muted)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Business</label>
            <input type="text" value={business} onChange={(e) => setBusiness(e.target.value)} className="sf-input" style={{ marginTop: 6, marginBottom: 16 }} required data-testid="impl-business-input" />

            <label className="sf-mono" style={{ fontSize: 12, color: "var(--sf-muted)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Preferred tier</label>
            <div style={{ display: "grid", gap: 8, marginTop: 6, marginBottom: 18 }} data-testid="impl-tier-group">
              {TIERS.map((t) => (
                <label
                  key={t.id}
                  className={`sf-choice ${tier === t.id ? "is-selected" : ""}`}
                  data-testid={`impl-tier-${t.id}`}
                >
                  <input
                    type="radio"
                    name="tier"
                    value={t.id}
                    checked={tier === t.id}
                    onChange={() => setTier(t.id)}
                    style={{ display: "none" }}
                  />
                  <span className="sf-choice-dot" />
                  <span>{t.label}</span>
                </label>
              ))}
            </div>

            <label className="sf-mono" style={{ fontSize: 12, color: "var(--sf-muted)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Notes (optional)</label>
            <textarea
              className="sf-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything specific you want addressed first?"
              style={{ marginTop: 6 }}
              data-testid="impl-notes-input"
            />

            {error && (
              <div className="sf-mono" style={{ color: "#e2867e", fontSize: 13, marginTop: 14 }} data-testid="impl-modal-error">
                {error}
              </div>
            )}

            <div style={{ display: "flex", gap: 12, marginTop: 22 }}>
              <button type="button" className="sf-btn-secondary" onClick={onClose} data-testid="impl-modal-cancel">
                Cancel
              </button>
              <button type="submit" className="sf-btn" disabled={submitting} data-testid="impl-modal-submit">
                {submitting ? "Submitting…" : "Submit request"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
